/**
 * The seat this device is acting as after a hand-over (the other officer's PIN, 10 minutes): kept in memory for the page session, so the header can say so on every page and Review reads the same answer.
 * The server also records it (`me.actingFor`), so a reload brings it back until it ends; Hand back is kept in this page only (no release route yet).
 */
import { useSyncExternalStore } from "react";
import type { Handoff } from "../signoff/SeatHandoff";
import { roleLabel } from "./TopBar";
import { useToast } from "../theme/Toast";
import { useNow } from "./useNow";

let current: (Handoff & { readonly userId: string }) | null = null;
const listeners = new Set<() => void>();
const notify = (): void => listeners.forEach((l) => l());

/** The server's record of the hand-over (`TeamState.me.actingFor`), which survives a reload; `until` is the instant it ends. */
export type ServerActing = { readonly role: "ceo" | "cro"; readonly until: string } | undefined;
/** The server's hand-over the person handed back by hand (its `until` is its identity), so the chip does not return on the next read. */
let handedBack: string | null = null;

/** `userId` is the signed-in person the hand-over was made on: another person on the same tab never inherits it. */
export function setActingAs(h: Handoff | null, userId = "", serverUntil: string | null = null): void { current = h === null ? null : { ...h, userId }; handedBack = h === null ? serverUntil : null; notify(); }
const snapshot = (): typeof current => current;
const handedBackSnapshot = (): string | null => handedBack;
/** The hand-over in force: this page's own, else the server's `actingFor` (so a reload keeps it) unless it was handed back. */
export function useActingAs(userId = "", server?: ServerActing): Handoff | null {
  const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
  const c = useSyncExternalStore(subscribe, snapshot);
  const back = useSyncExternalStore(subscribe, handedBackSnapshot);
  if (c !== null && c.userId === userId) return c;
  return server === undefined || server.until === back ? null : { role: server.role, expiresAt: server.until };
}

/** The role this device is working as: the hand-over's role while it lasts, otherwise the signed-in person's own: the one state behind the chip and the header's role badge. */
export function useWorkingRole(ownRole: string | null, userId = "", server?: ServerActing): string | null {
  const handoff = useActingAs(userId, server);
  const now = useNow(15_000);
  return handoff !== null && Date.parse(handoff.expiresAt) > now ? handoff.role : ownRole;
}

const WORDS: Readonly<Record<string, string>> = { cro: "the risk officer", ceo: "the CEO" };

/** A full-width strip under the top bar: "Acting as the risk officer · 10 min left", with Hand back. Nothing once the hand-over has run out. */
export function ActingAsChip({ ownRole, userId = "", server }: { readonly ownRole: string | null; readonly userId?: string; readonly server?: ServerActing }) {
  const handoff = useActingAs(userId, server);
  const now = useNow(15_000);
  const toast = useToast();
  if (handoff === null) return null;
  const ms = Date.parse(handoff.expiresAt) - now;
  if (ms <= 0) return null;
  const own = ownRole === null || ownRole === handoff.role ? "your own seat" : WORDS[ownRole] ?? roleLabel(ownRole);
  return (
    <div className="gx-acting-strip" role="status" data-testid="shell.actingAs">
      <span>Acting as <b>{WORDS[handoff.role] ?? roleLabel(handoff.role)}</b> · {Math.max(1, Math.ceil(ms / 60_000))} min left. Hand back to return to {own}.</span>
      <button type="button" className="gx-btn gx-btn--onnavy" data-testid="shell.actingAs.back" onClick={() => { setActingAs(null, userId, server?.until ?? null); toast(`You are ${own === "your own seat" ? "back in your own seat" : own} again.`); }}>Hand back</button>
    </div>
  );
}
